import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { UserPlus, CalendarCheck, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackBooking } from '@/utils/analytics';

const AVENYX_URL = 'https://avenyx.ai/portal/signup?type=student';

const NextStepsSection = () => {
  const { t, i18n } = useTranslation();
  const calendlyUrl =
    i18n.language === 'pt'
      ? 'https://calendly.com/d/cxsd-rm9-hsx/consulta-para-estrategia-academica-portuguese'
      : 'https://calendly.com/d/cxsd-r9h-zc4/academic-strategy-consultation-english';

  const steps = [
    {
      icon: UserPlus,
      title: t('nextSteps.step1.title', 'Register on Avenyx.Ai'),
      description: t(
        'nextSteps.step1.description',
        'Create your student account and use the agency code CONEXUS so your application is linked to our team.',
      ),
      cta: t('nextSteps.step1.cta', 'Register now'),
      href: AVENYX_URL,
      onClick: undefined as (() => void) | undefined,
    },
    {
      icon: CalendarCheck,
      title: t('nextSteps.step2.title', 'Book a Consultation'),
      description: t(
        'nextSteps.step2.description',
        'Talk with one of our Academic Consultants and build the best plan for your journey in the USA.',
      ),
      cta: t('nextSteps.step2.cta', 'Book a Consultation'),
      href: calendlyUrl,
      onClick: () => trackBooking(),
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-muted/40">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-conexus-gold/15 border border-conexus-gold/40 text-conexus-navy text-sm font-semibold">
            ✦ {t('nextSteps.badge', 'Interested in a program?')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t('nextSteps.title', 'Your next steps')}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t('nextSteps.subtitle', 'Two simple steps to start your academic journey with Conexus.')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative flex flex-col h-full rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm"
            >
              <span className="absolute -top-3 left-6 inline-flex h-7 w-7 items-center justify-center rounded-full bg-conexus-navy text-xs font-bold text-white">
                {index + 1}
              </span>
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-conexus-gold/15 text-conexus-navy">
                <step.icon className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
              <p className="text-muted-foreground mb-6 flex-1">{step.description}</p>
              <Button
                asChild
                className="w-full bg-conexus-navy hover:bg-conexus-navy/90 text-white font-semibold"
                onClick={step.onClick}
              >
                <a href={step.href} target="_blank" rel="noopener noreferrer">
                  {step.cta}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t('nextSteps.codeNote', 'Use the agency code')}{' '}
          <span className="font-bold text-conexus-navy">CONEXUS</span>
        </p>
      </div>
    </section>
  );
};

export default NextStepsSection;
